import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Component } from '@/types';
import type { GanttFieldMapping } from '@/types/pm-types';
import { useDataWriteback } from '@/hooks/useDataWriteback';
import { useAutomationRules } from '@/hooks/useAutomationRules';
import { useAuthStore } from '@/stores/auth/auth-store';
import { exportToCSV } from '@/utils/pmDataExport';
import type { ComponentDataWriteOperation } from '@/types/componentDataBinding';
import { getDataBindingWriteTarget } from './dataBindingWriteTarget';
import {
  type TimeScale,
  type GanttTask,
  calculateTimeRange,
  extendRangeForScale,
  dateToPixel,
  pixelToDate,
  getTimeIntervals,
  calculateBarPosition,
  autoSelectTimeScale,
  calculateTotalWidth,
  getPixelsPerUnit,
} from './ganttTimeUtils';
import { GanttTimeHeader } from './GanttTimeHeader';
import { GanttBar } from './GanttBar';
import { GanttDependencyArrows } from './GanttDependencyArrows';
import { pmTheme } from './pmTheme';
import {
  readAutomationRules,
  readFiniteNumber,
  readGanttFieldMapping,
  readGanttTimeScale,
  readStringRecord,
  readWritebackConfig,
} from './pmPropertyReaders';

// ---------- Types ----------

export interface GanttChartProps {
  component: Component;
  data: Record<string, unknown>[];
  isViewerMode: boolean;
  writeTargetPermissions?: Partial<Record<ComponentDataWriteOperation, boolean>>;
  writeTargetRequestingOrganizationId?: string | null;
  onAddTask?: () => void;
  onEditTask?: (taskId: string, taskData: Record<string, unknown>) => void;
}

interface GroupSection {
  name: string;
  tasks: GanttTask[];
  collapsed: boolean;
}

// ---------- Defaults ----------

export const DEFAULT_FIELD_MAPPING: GanttFieldMapping = {
  id: 'id',
  taskName: 'name',
  startDate: 'start',
  endDate: 'end',
  progress: 'progress',
  dependencies: 'deps',
  group: 'group',
  milestone: 'milestone',
};

export const DEFAULT_SAMPLE_DATA: Record<string, unknown>[] = [
  { id: '1', name: 'Wireframes', start: '2026-03-03', end: '2026-03-10', progress: 100, group: 'Design', deps: [], milestone: false },
  { id: '2', name: 'UI Design', start: '2026-03-07', end: '2026-03-17', progress: 75, group: 'Design', deps: ['1'], milestone: false },
  { id: '3', name: 'Design Review', start: '2026-03-17', end: '2026-03-17', milestone: true, group: 'Design', deps: ['2'], progress: 0 },
  { id: '4', name: 'Frontend Dev', start: '2026-03-17', end: '2026-03-28', progress: 30, group: 'Development', deps: ['3'], milestone: false },
  { id: '5', name: 'Backend Dev', start: '2026-03-19', end: '2026-04-01', progress: 20, group: 'Development', deps: ['3'], milestone: false },
  { id: '6', name: 'QA Testing', start: '2026-03-31', end: '2026-04-10', progress: 0, group: 'Testing', deps: ['4', '5'], milestone: false },
];

const GANTT_TIME_SCALES: TimeScale[] = ['day', 'week', 'month', 'quarter'];

// ---------- Helpers ----------

function parseDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(String(value));
  return isNaN(d.getTime()) ? null : d;
}

function parseDeps(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') {
    return value.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
}

function transformToTasks(
  data: Record<string, unknown>[],
  fm: GanttFieldMapping,
): GanttTask[] {
  return data.map(record => ({
    id: String(record[fm.id] ?? Math.random().toString(36).slice(2)),
    name: String(record[fm.taskName] ?? ''),
    startDate: parseDate(record[fm.startDate]),
    endDate: fm.endDate ? parseDate(record[fm.endDate]) : null,
    duration: fm.duration ? Number(record[fm.duration]) || undefined : undefined,
    progress: fm.progress ? Number(record[fm.progress]) || 0 : undefined,
    dependencies: fm.dependencies ? parseDeps(record[fm.dependencies]) : [],
    group: fm.group ? String(record[fm.group] ?? '') : undefined,
    milestone: fm.milestone ? Boolean(record[fm.milestone]) : false,
    assignee: fm.assignee ? String(record[fm.assignee] ?? '') : undefined,
    color: fm.color ? String(record[fm.color] ?? '') || undefined : undefined,
    parentId: fm.parentId ? String(record[fm.parentId] ?? '') || undefined : undefined,
    iteration: fm.iteration ? String(record[fm.iteration] ?? '') || undefined : undefined,
  }));
}

function buildGroups(tasks: GanttTask[], collapsedGroups: Set<string>): GroupSection[] {
  const groupMap = new Map<string, GanttTask[]>();
  const ungrouped: GanttTask[] = [];

  for (const task of tasks) {
    if (task.group) {
      const arr = groupMap.get(task.group) ?? [];
      arr.push(task);
      groupMap.set(task.group, arr);
    } else {
      ungrouped.push(task);
    }
  }

  const groups: GroupSection[] = [];
  for (const [name, groupTasks] of groupMap) {
    groups.push({ name, tasks: groupTasks, collapsed: collapsedGroups.has(name) });
  }
  if (ungrouped.length > 0) {
    groups.push({ name: 'Ungrouped', tasks: ungrouped, collapsed: collapsedGroups.has('Ungrouped') });
  }
  return groups;
}

function buildHierarchy(tasks: GanttTask[]): { parents: GanttTask[]; childMap: Map<string, GanttTask[]> } {
  const childMap = new Map<string, GanttTask[]>();
  const parentIds = new Set<string>();

  for (const task of tasks) {
    if (task.parentId) {
      const children = childMap.get(task.parentId) ?? [];
      children.push(task);
      childMap.set(task.parentId, children);
      parentIds.add(task.parentId);
    }
  }

  const parents = tasks.filter(t => !t.parentId);
  return { parents, childMap };
}

// ---------- Component ----------

export const GanttChart: React.FC<GanttChartProps> = ({
  component,
  data,
  isViewerMode,
  writeTargetPermissions,
  writeTargetRequestingOrganizationId,
  onAddTask,
  onEditTask,
}) => {
  const properties = component.properties;

  // Extract properties with defaults
  const fieldMapping = readGanttFieldMapping(properties.fieldMapping, DEFAULT_FIELD_MAPPING);
  const initialTimeScale = readGanttTimeScale(properties.timeScale);
  const showTodayLine = properties.showTodayLine !== false;
  const showGridLines = properties.showGridLines !== false;
  const showProgress = properties.showProgress !== false;
  const showDependencies = properties.showDependencies !== false;
  const showMilestones = properties.showMilestones !== false;
  const showIterationBands = Boolean(properties.showIterationBands);
  const showUndatedItems = properties.showUndatedItems !== false;
  const showExportButton = Boolean(properties.showExportButton);
  const showCriticalPath = Boolean(properties.showCriticalPath);
  const rowHeight = readFiniteNumber(properties.rowHeight, 36);
  const barHeight = readFiniteNumber(properties.barHeight, 24);
  const barCornerRadius = readFiniteNumber(properties.barCornerRadius, 4);
  const labelColumnWidth = readFiniteNumber(properties.labelColumnWidth, 200);
  const barColor = String(properties.barColor || '#3B82F6');
  const progressColor = String(properties.progressColor || '#2563EB');
  const todayLineColor = String(properties.todayLineColor || '#EF4444');
  const criticalPathColor = String(properties.criticalPathColor || '#EF4444');
  const barDragEnabled = Boolean(properties.barDragEnabled) && isViewerMode;
  const barResizeEnabled = Boolean(properties.barResizeEnabled) && isViewerMode;
  const writeback = readWritebackConfig(properties.writeback);
  const colorMapping = readStringRecord(properties.colorMapping);
  const dataBindingWriteTarget = useMemo(
    () => getDataBindingWriteTarget(properties.dataBinding),
    [properties.dataBinding],
  );

  // State
  const [timeScale, setTimeScale] = useState<TimeScale>(initialTimeScale);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [collapsedParents, setCollapsedParents] = useState<Set<string>>(new Set());
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [detailTask, setDetailTask] = useState<GanttTask | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  // Timeline drag-to-pan state (refs to avoid re-renders during drag)
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // Writeback hook
  const { updateRecord } = useDataWriteback({
    writebackConfig: writeback,
    writeTarget: dataBindingWriteTarget.writeTarget,
    requireExplicitWriteTarget:
      dataBindingWriteTarget.requireExplicitWriteTarget,
    writePermissions: writeTargetPermissions,
    writeTargetRequestingOrganizationId,
    componentId: component.id,
  });

  // Automation rules
  const user = useAuthStore((s) => s.user);
  const { handleDataEvent, detectNewRecords } = useAutomationRules({
    rules: readAutomationRules(properties.automationRules),
    writebackConfig: writeback,
    componentId: component.id,
    fieldMapping,
    currentUser: user?.firstName ?? user?.username,
    enabled: isViewerMode,
  });

  // Detect new records for onItemAdd automation rules
  useEffect(() => {
    if (isViewerMode) detectNewRecords(data);
  }, [data, isViewerMode, detectNewRecords]);

  // Transform data → tasks
  const allTasks = useMemo(() => transformToTasks(data, fieldMapping), [data, fieldMapping]);

  // Separate dated and undated tasks
  const { datedTasks, undatedTasks } = useMemo(() => {
    const dated: GanttTask[] = [];
    const undated: GanttTask[] = [];
    for (const t of allTasks) {
      if (t.startDate) dated.push(t);
      else undated.push(t);
    }
    return { datedTasks: dated, undatedTasks: undated };
  }, [allTasks]);

  // Filter milestones if hidden
  const visibleTasks = useMemo(() => {
    if (showMilestones) return datedTasks;
    return datedTasks.filter(t => !t.milestone);
  }, [datedTasks, showMilestones]);

  // Apply color mapping
  const coloredTasks = useMemo(() => {
    if (!colorMapping) return visibleTasks;
    return visibleTasks.map(t => {
      if (t.group && colorMapping[t.group]) return { ...t, color: colorMapping[t.group] };
      return t;
    });
  }, [visibleTasks, colorMapping]);

  // Build groups
  const groups = useMemo(() => buildGroups(coloredTasks, collapsedGroups), [coloredTasks, collapsedGroups]);

  // Time range and intervals
  const rawTimeRange = useMemo(() => calculateTimeRange(coloredTasks), [coloredTasks]);
  const timeRange = useMemo(() => extendRangeForScale(rawTimeRange, timeScale), [rawTimeRange, timeScale]);
  const basePpu = getPixelsPerUnit(timeScale);
  // Scale up pixelsPerUnit when the data range is narrower than the container
  const baseTotalWidth = useMemo(
    () => calculateTotalWidth(timeRange.start, timeRange.end, timeScale, basePpu),
    [timeRange, timeScale, basePpu],
  );
  const ppu = containerWidth > 0 && baseTotalWidth < containerWidth
    ? basePpu * (containerWidth / baseTotalWidth)
    : basePpu;
  const intervals = useMemo(
    () => getTimeIntervals(timeRange.start, timeRange.end, timeScale),
    [timeRange, timeScale],
  );
  const totalWidth = useMemo(
    () => calculateTotalWidth(timeRange.start, timeRange.end, timeScale, ppu),
    [timeRange, timeScale, ppu],
  );

  // Calculate task positions for bars and arrows
  const { taskPositions, totalHeight } = useMemo(() => {
    const positions = new Map<string, { id: string; x: number; width: number; y: number; barHeight: number }>();
    let currentY = 0;
    const headerHeight = 40;
    currentY += headerHeight;

    for (const group of groups) {
      currentY += rowHeight; // Group header row
      if (!group.collapsed) {
        const { parents, childMap } = buildHierarchy(group.tasks);
        for (const task of parents) {
          const { x, width } = calculateBarPosition(task, timeScale, timeRange.start, ppu);
          positions.set(task.id, { id: task.id, x, width, y: currentY + (rowHeight - barHeight) / 2, barHeight });
          currentY += rowHeight;

          // Children
          if (!collapsedParents.has(task.id)) {
            const children = childMap.get(task.id) ?? [];
            for (const child of children) {
              const cp = calculateBarPosition(child, timeScale, timeRange.start, ppu);
              positions.set(child.id, { id: child.id, x: cp.x, width: cp.width, y: currentY + (rowHeight - barHeight) / 2, barHeight });
              currentY += rowHeight;
            }
          }
        }
      }
    }

    return { taskPositions: positions, totalHeight: Math.max(currentY + 20, 200) };
  }, [groups, coloredTasks, timeScale, timeRange, ppu, rowHeight, barHeight, collapsedParents]);

  const headerHeight = 40;

  // Toggle group collapse
  const toggleGroup = useCallback((name: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  // Toggle parent collapse
  const toggleParent = useCallback((id: string) => {
    setCollapsedParents(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Bar click → edit modal (if available) or detail popup
  const handleBarClick = useCallback((task: GanttTask) => {
    if (!isViewerMode) return;
    if (onEditTask) {
      // Find the raw data record for this task
      const rawRecord = data.find((row) => String(row[fieldMapping.id]) === task.id);
      if (rawRecord) {
        onEditTask(task.id, rawRecord);
      }
    } else {
      setDetailTask(task);
    }
  }, [isViewerMode, onEditTask, data, fieldMapping]);

  // Drag-to-reschedule handlers
  const handleDragEnd = useCallback((taskId: string, deltaX: number) => {
    if (!barDragEnabled) return;
    const pos = taskPositions.get(taskId);
    if (!pos) return;

    const task = allTasks.find(t => t.id === taskId);
    if (!task?.startDate) return;

    const newStart = pixelToDate(pos.x + deltaX, timeScale, timeRange.start, ppu);
    const duration = task.endDate
      ? (task.endDate.getTime() - task.startDate.getTime())
      : 86400000;
    const newEnd = new Date(newStart.getTime() + duration);

    const rawRecord = data.find((row) => String(row[fieldMapping.id]) === taskId);
    updateRecord(taskId, {
      [fieldMapping.startDate]: newStart.toISOString().split('T')[0],
      ...(fieldMapping.endDate ? { [fieldMapping.endDate]: newEnd.toISOString().split('T')[0] } : {}),
    });

    // Fire automation rules for field change (date reschedule)
    if (rawRecord) {
      handleDataEvent({
        type: 'onFieldChange',
        record: { ...rawRecord, [fieldMapping.startDate]: newStart.toISOString().split('T')[0] },
        previousRecord: rawRecord,
        field: fieldMapping.startDate,
        oldValue: rawRecord[fieldMapping.startDate],
        newValue: newStart.toISOString().split('T')[0],
      }).catch(console.error);
    }
  }, [barDragEnabled, taskPositions, allTasks, timeScale, timeRange, ppu, updateRecord, fieldMapping, data, handleDataEvent]);

  // Resize handler
  const handleResizeEnd = useCallback((taskId: string, deltaWidth: number) => {
    if (!barResizeEnabled) return;
    const pos = taskPositions.get(taskId);
    if (!pos) return;

    const task = allTasks.find(t => t.id === taskId);
    if (!task?.startDate) return;

    const newEndX = pos.x + pos.width + deltaWidth;
    const newEnd = pixelToDate(newEndX, timeScale, timeRange.start, ppu);

    if (fieldMapping.endDate) {
      const rawRecord = data.find((row) => String(row[fieldMapping.id]) === taskId);
      updateRecord(taskId, {
        [fieldMapping.endDate]: newEnd.toISOString().split('T')[0],
      });

      // Fire automation rules for field change (end date resize)
      if (rawRecord) {
        handleDataEvent({
          type: 'onFieldChange',
          record: { ...rawRecord, [fieldMapping.endDate]: newEnd.toISOString().split('T')[0] },
          previousRecord: rawRecord,
          field: fieldMapping.endDate,
          oldValue: rawRecord[fieldMapping.endDate],
          newValue: newEnd.toISOString().split('T')[0],
        }).catch(console.error);
      }
    }
  }, [barResizeEnabled, taskPositions, allTasks, timeScale, timeRange, ppu, updateRecord, fieldMapping, data, handleDataEvent]);

  // Scroll to today
  const scrollToToday = useCallback(() => {
    if (!scrollRef.current) return;
    const todayX = dateToPixel(new Date(), timeScale, timeRange.start, ppu);
    scrollRef.current.scrollLeft = todayX - scrollRef.current.clientWidth / 2;
  }, [timeScale, timeRange, ppu]);

  // Export handler
  const handleExport = useCallback(() => {
    const visibleFields = ['id', 'taskName', 'startDate', 'endDate', 'progress', 'group', 'assignee'];
    exportToCSV(data, fieldMapping, visibleFields, `gantt-export-${Date.now()}`);
  }, [data, fieldMapping]);

  // Drag-to-pan on the timeline
  const handleTimelineMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // left button only
    const el = scrollRef.current;
    if (!el) return;

    isPanningRef.current = true;
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: el.scrollLeft,
      scrollTop: el.scrollTop,
    };
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';

    const handleMouseMove = (moveEvt: MouseEvent) => {
      if (!isPanningRef.current) return;
      const dx = moveEvt.clientX - panStartRef.current.x;
      const dy = moveEvt.clientY - panStartRef.current.y;
      el.scrollLeft = panStartRef.current.scrollLeft - dx;
      el.scrollTop = panStartRef.current.scrollTop - dy;
      if (labelRef.current) {
        labelRef.current.scrollTop = el.scrollTop;
      }
    };

    const handleMouseUp = () => {
      isPanningRef.current = false;
      el.style.cursor = 'grab';
      el.style.removeProperty('user-select');
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Sync label column vertical scroll when timeline scrolls normally (mousewheel, etc.)
  const handleTimelineScroll = useCallback(() => {
    if (labelRef.current && scrollRef.current) {
      labelRef.current.scrollTop = scrollRef.current.scrollTop;
    }
  }, []);

  // Track scroll container width so SVG + grid fill the visible area
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Auto-scroll to today on mount
  useEffect(() => {
    const timer = setTimeout(scrollToToday, 100);
    return () => clearTimeout(timer);
  }, [scrollToToday]);

  // Build the SVG content for all groups
  const renderGroups = () => {
    const elements: React.ReactNode[] = [];
    let currentY = headerHeight;

    for (const group of groups) {
      // Group header
      elements.push(
        <g
          key={`group-header-${group.name}`}
          onClick={() => toggleGroup(group.name)}
          style={{ cursor: 'pointer' }}
          data-testid={`gantt-group-header-${group.name}`}
        >
          <rect x={0} y={currentY} width={Math.max(totalWidth, 600)} height={rowHeight} fill={pmTheme.surfaceMuted} opacity={0.5} />
          <text x={8} y={currentY + rowHeight / 2 + 4} fontSize={12} fontWeight={600} fill={pmTheme.text}>
            {group.collapsed ? '▶' : '▼'} {group.name} ({group.tasks.length})
          </text>
        </g>,
      );
      currentY += rowHeight;

      if (!group.collapsed) {
        const { parents, childMap } = buildHierarchy(group.tasks);

        for (const task of parents) {
          const hasChildren = childMap.has(task.id);
          const pos = taskPositions.get(task.id);
          if (pos) {
            elements.push(
              <GanttBar
                key={`bar-${task.id}`}
                task={task}
                x={pos.x}
                width={pos.width}
                y={pos.y}
                barHeight={barHeight}
                barColor={barColor}
                progressColor={progressColor}
                barCornerRadius={barCornerRadius}
                isMilestone={task.milestone}
                isSummary={hasChildren}
                isDraggable={barDragEnabled}
                isResizable={barResizeEnabled}
                showProgress={showProgress}
                labelColumnWidth={labelColumnWidth}
                onClick={handleBarClick}
                onDragEnd={handleDragEnd}
                onResizeEnd={handleResizeEnd}
              />,
            );
          }
          currentY += rowHeight;

          // Children
          if (!collapsedParents.has(task.id)) {
            const children = childMap.get(task.id) ?? [];
            for (const child of children) {
              const cp = taskPositions.get(child.id);
              if (cp) {
                elements.push(
                  <GanttBar
                    key={`bar-${child.id}`}
                    task={child}
                    x={cp.x}
                    width={cp.width}
                    y={cp.y}
                    barHeight={barHeight - 4}
                    barColor={barColor}
                    progressColor={progressColor}
                    barCornerRadius={barCornerRadius}
                    isMilestone={child.milestone}
                    isSummary={false}
                    isDraggable={barDragEnabled}
                    isResizable={barResizeEnabled}
                    showProgress={showProgress}
                    labelColumnWidth={labelColumnWidth}
                    onClick={handleBarClick}
                    onDragEnd={handleDragEnd}
                    onResizeEnd={handleResizeEnd}
                  />,
                );
              }
              currentY += rowHeight;
            }
          }
        }
      }
    }

    return elements;
  };

  // Build label column rows
  const renderLabels = () => {
    const elements: React.ReactNode[] = [];
    let currentY = headerHeight;

    for (const group of groups) {
      elements.push(
        <div
          key={`label-group-${group.name}`}
          onClick={() => toggleGroup(group.name)}
          style={{
            height: rowHeight,
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            backgroundColor: pmTheme.surfaceMuted,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 12,
            color: pmTheme.text,
            borderBottom: `1px solid ${pmTheme.border}`,
          }}
          data-testid={`gantt-label-group-${group.name}`}
        >
          {group.collapsed ? '▶' : '▼'} {group.name}
        </div>,
      );
      currentY += rowHeight;

      if (!group.collapsed) {
        const { parents, childMap } = buildHierarchy(group.tasks);
        for (const task of parents) {
          const hasChildren = childMap.has(task.id);
          elements.push(
            <div
              key={`label-${task.id}`}
              onClick={() => hasChildren && toggleParent(task.id)}
              style={{
                height: rowHeight,
                display: 'flex',
                alignItems: 'center',
                padding: '0 8px',
                paddingLeft: hasChildren ? 8 : 16,
                fontSize: 12,
                color: pmTheme.text,
                fontWeight: hasChildren ? 600 : 400,
                cursor: hasChildren ? 'pointer' : 'default',
                borderBottom: `1px solid ${pmTheme.borderSubtle}`,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
              title={task.name}
            >
              {hasChildren && (collapsedParents.has(task.id) ? '▶ ' : '▼ ')}
              {task.milestone ? '◆ ' : ''}
              {task.name}
            </div>,
          );
          currentY += rowHeight;

          if (!collapsedParents.has(task.id)) {
            const children = childMap.get(task.id) ?? [];
            for (const child of children) {
              elements.push(
                <div
                  key={`label-${child.id}`}
                  style={{
                    height: rowHeight,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px 0 28px',
                    fontSize: 11,
                    color: pmTheme.textMuted,
                    borderBottom: `1px solid ${pmTheme.borderSubtle}`,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                  title={child.name}
                >
                  {child.milestone ? '◆ ' : ''}
                  {child.name}
                </div>,
              );
              currentY += rowHeight;
            }
          }
        }
      }
    }

    return elements;
  };

  return (
    <div
      data-testid="gantt-chart"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'Inter, system-ui, sans-serif',
        backgroundColor: pmTheme.surface,
        color: pmTheme.text,
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          borderBottom: `1px solid ${pmTheme.border}`,
          flexShrink: 0,
        }}
        data-testid="gantt-toolbar"
      >
        {/* Title */}
        {typeof properties.title === 'string' && properties.title && (
          <span style={{ fontSize: 14, fontWeight: 600, color: pmTheme.text, marginRight: 8 }}>
            {properties.title}
          </span>
        )}

        {/* Time scale buttons */}
        {GANTT_TIME_SCALES.map(scale => (
          <button
            key={scale}
            onClick={() => setTimeScale(scale)}
            data-testid={`gantt-scale-${scale}`}
            style={{
              padding: '3px 10px',
              fontSize: 11,
              borderRadius: 4,
              border: timeScale === scale ? `1px solid ${pmTheme.primary}` : `1px solid ${pmTheme.input}`,
              backgroundColor: timeScale === scale ? pmTheme.primarySoft : pmTheme.surface,
              color: timeScale === scale ? pmTheme.primary : pmTheme.textMuted,
              cursor: 'pointer',
              fontWeight: timeScale === scale ? 600 : 400,
            }}
          >
            {scale.charAt(0).toUpperCase() + scale.slice(1)}
          </button>
        ))}

        {/* Scroll to today */}
        <button
          onClick={scrollToToday}
          data-testid="gantt-scroll-to-today"
          style={{
            padding: '3px 10px',
            fontSize: 11,
            borderRadius: 4,
            border: `1px solid ${pmTheme.input}`,
            backgroundColor: pmTheme.surface,
            color: pmTheme.textMuted,
            cursor: 'pointer',
            marginLeft: 4,
          }}
        >
          Today
        </button>

        <div style={{ flex: 1 }} />

        {/* Add Task button */}
        {onAddTask && (
          <button
            onClick={onAddTask}
            data-testid="gantt-add-task-button"
            style={{
              padding: '3px 10px',
              fontSize: 11,
              borderRadius: 4,
              border: `1px solid ${pmTheme.successBorder}`,
              backgroundColor: pmTheme.successSoft,
              color: pmTheme.success,
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            + Add Task
          </button>
        )}

        {/* Export button */}
        {showExportButton && (
          <button
            onClick={handleExport}
            data-testid="gantt-export-button"
            style={{
              padding: '3px 10px',
              fontSize: 11,
              borderRadius: 4,
              border: `1px solid ${pmTheme.input}`,
              backgroundColor: pmTheme.surface,
              color: pmTheme.textMuted,
              cursor: 'pointer',
            }}
          >
            Export CSV
          </button>
        )}
      </div>

      {/* Main body: label column + SVG timeline */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Label column */}
        <div
          style={{
            width: labelColumnWidth,
            flexShrink: 0,
            borderRight: `1px solid ${pmTheme.border}`,
            overflowY: 'hidden',
            overflowX: 'hidden',
          }}
          ref={labelRef}
          data-testid="gantt-label-column"
        >
          {/* Label header */}
          <div
            style={{
              height: headerHeight,
              display: 'flex',
              alignItems: 'center',
              padding: '0 8px',
              fontSize: 11,
              fontWeight: 600,
              color: pmTheme.textMuted,
              backgroundColor: pmTheme.surfaceSubtle,
              borderBottom: `1px solid ${pmTheme.border}`,
            }}
          >
            Task
          </div>
          {renderLabels()}
        </div>

        {/* Scrollable SVG timeline */}
        <div
          ref={scrollRef}
          onMouseDown={handleTimelineMouseDown}
          onScroll={handleTimelineScroll}
          style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', cursor: 'grab' }}
          data-testid="gantt-timeline-scroll"
        >
          <svg
            width={Math.max(totalWidth, 600)}
            height={totalHeight}
            data-testid="gantt-svg"
          >
            {/* Time header (axis labels, grid lines, today line) */}
            <GanttTimeHeader
              startDate={timeRange.start}
              endDate={timeRange.end}
              timeScale={timeScale}
              intervals={intervals}
              showTodayLine={showTodayLine}
              showGridLines={showGridLines}
              showIterationBands={showIterationBands}
              totalWidth={Math.max(totalWidth, 600)}
              headerHeight={headerHeight}
              bodyHeight={totalHeight - headerHeight}
              todayLineColor={todayLineColor}
            />

            {/* Task bars */}
            {renderGroups()}

            {/* Dependency arrows */}
            {showDependencies && (
              <GanttDependencyArrows
                tasks={coloredTasks}
                taskPositions={taskPositions}
                barHeight={barHeight}
                showCriticalPath={showCriticalPath}
                criticalPathColor={criticalPathColor}
              />
            )}
          </svg>
        </div>
      </div>

      {/* Undated items section */}
      {showUndatedItems && undatedTasks.length > 0 && (
        <div
          style={{
            borderTop: `1px solid ${pmTheme.border}`,
            padding: '8px 12px',
            maxHeight: 120,
            overflowY: 'auto',
            flexShrink: 0,
          }}
          data-testid="gantt-undated-section"
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: pmTheme.textMuted, marginBottom: 4 }}>
            Undated Items ({undatedTasks.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {undatedTasks.map(task => (
              <div
                key={task.id}
                style={{
                  padding: '2px 8px',
                  fontSize: 11,
                  backgroundColor: pmTheme.surfaceMuted,
                  borderRadius: 4,
                  color: pmTheme.text,
                }}
              >
                {task.milestone ? '◆ ' : ''}{task.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailTask && (
        <div
          data-testid="gantt-detail-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => setDetailTask(null)}
        >
          <div
            style={{
              backgroundColor: pmTheme.surface,
              color: pmTheme.text,
              borderRadius: 8,
              padding: 24,
              width: 360,
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: 'var(--shadow-xl)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: pmTheme.text, margin: 0 }}>{detailTask.name}</h3>
              <button
                onClick={() => setDetailTask(null)}
                style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: pmTheme.textMuted }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: pmTheme.textMuted }}>
              {detailTask.startDate && <div><strong>Start:</strong> {detailTask.startDate.toLocaleDateString()}</div>}
              {detailTask.endDate && <div><strong>End:</strong> {detailTask.endDate.toLocaleDateString()}</div>}
              {detailTask.progress !== undefined && <div><strong>Progress:</strong> {detailTask.progress}%</div>}
              {detailTask.group && <div><strong>Group:</strong> {detailTask.group}</div>}
              {detailTask.assignee && <div><strong>Assignee:</strong> {detailTask.assignee}</div>}
              {detailTask.milestone && <div><strong>Type:</strong> Milestone</div>}
              {detailTask.dependencies.length > 0 && (
                <div><strong>Dependencies:</strong> {detailTask.dependencies.join(', ')}</div>
              )}
              {detailTask.iteration && <div><strong>Iteration:</strong> {detailTask.iteration}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

GanttChart.displayName = 'GanttChart';
